import { useState } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { Banner, Button } from '@/components/primitives'
import { DetailRow, DetailRows, SummaryCard } from '@/components/cards'
import { BarChart, type BarDatum } from '@/components/charts/BarChart'
import { List, TransactionRow } from '@/components/rows'
import { ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { cadenceName, categoryName } from '@/lib/display'
import { formatDate, formatDayMonth, formatMoney } from '@/lib/format'
import { useNavigate, useRoute } from '@/lib/router'
import { categoryColorVar } from '@/lib/tokens'
import { useAsync } from '@/lib/useAsync'

/** One recurring series: cadence, price history, next charge, what to do. */
export function RecurringDetailScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const id = route.params.id

  const series = useAsync(() => api.analysis.recurringSeries(id), [id])
  const transactions = useAsync(
    (signal) => api.transactions.list({ recurring: true, limit: 100 }, signal),
    [id],
  )

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)

  const data = series.state.data
  const screenState = saving
    ? 'saving'
    : error
      ? 'error'
      : series.state.status === 'loading' && !data
        ? 'loading'
        : series.state.status === 'error'
          ? 'error'
          : 'ready'

  const update = async (patch: { status?: 'cancelledByUser'; userConfirmed?: boolean }) => {
    setSaving(true)
    setError(null)
    try {
      const updated = await api.analysis.updateRecurring(id, patch)
      series.setData(updated)
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="recurringDetail" state={screenState} back={{ screen: 'recurring' }}>
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonList rows={5} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (!data) {
    return (
      <Screen id="recurringDetail" state="error" back={{ screen: 'recurring' }}>
        <ErrorState
          titleKey="recurringDetail.error.title"
          error={series.state.status === 'error' ? series.state.error : undefined}
          onRetry={series.reload}
        />
      </Screen>
    )
  }

  const history = data.priceHistory ?? []
  const first = history[0]
  const last = history.at(-1)
  const priceChanged = history.length > 1 && first && last && first.amount.amount !== last.amount.amount

  const bars: BarDatum[] = history.map((entry) => ({
    label: formatDayMonth(entry.effectiveFrom),
    series: [
      {
        key: entry.effectiveFrom,
        value: Math.abs(entry.amount.amount),
        color: categoryColorVar(data.category ?? 'subscriptions'),
        label: data.name,
      },
    ],
  }))

  const rows = (transactions.state.data?.items ?? []).filter((tx) =>
    data.transactionIds.includes(tx.id),
  )

  return (
    <Screen id="recurringDetail" state={screenState} title={data.name} back={{ screen: 'recurring' }}>
      <div className="stack">
        {error ? (
          <ErrorState titleKey="recurringDetail.error.title" error={error} onRetry={() => setError(null)} />
        ) : null}

        {priceChanged ? (
          <Banner tone="caution">
            {t('recurringDetail.priceChange', {
              before: formatMoney(first.amount, { absolute: true }),
              after: formatMoney(last.amount, { absolute: true }),
              month: formatDate(last.effectiveFrom),
            })}
          </Banner>
        ) : null}

        <SummaryCard>
          <DetailRows>
            <DetailRow label={t('recurringDetail.amount')}>
              {formatMoney(data.typicalAmount, { absolute: true })}
            </DetailRow>
            <DetailRow label={t('recurringDetail.cadence')}>{cadenceName(data.cadence)}</DetailRow>
            <DetailRow label={t('transactionDetail.category')}>
              {categoryName(data.category ?? 'subscriptions')}
            </DetailRow>
            {data.firstSeen ? (
              <DetailRow label={t('recurringDetail.since')}>{formatDate(data.firstSeen)}</DetailRow>
            ) : null}
            {data.nextExpected ? (
              <DetailRow label={t('recurringDetail.next')}>{formatDate(data.nextExpected)}</DetailRow>
            ) : null}
            <DetailRow label={t('recurringDetail.annualCost')}>
              {formatMoney(data.annualCost, { absolute: true })}
            </DetailRow>
            <DetailRow label={t('recurringDetail.paidToDate')}>
              {formatMoney(data.paidToDate, { absolute: true })}
            </DetailRow>
            {data.lastCharged ? (
              <DetailRow label={t('recurringDetail.lastCharge', { date: formatDate(data.lastCharged) })}>
                {formatDate(data.lastCharged)}
              </DetailRow>
            ) : null}
          </DetailRows>
        </SummaryCard>

        {bars.length > 1 ? (
          <div className="card card-pad-lg">
            <BarChart
              data={bars}
              height={160}
              caption={data.name}
              formatValue={(value) =>
                formatMoney({ amount: value, currency: data.typicalAmount.currency }, { absolute: true })
              }
            />
          </div>
        ) : null}

        <section>
          <SectionHeader title={t('recurringDetail.section.history')} />
          {transactions.state.status === 'loading' && rows.length === 0 ? (
            <SkeletonList rows={4} />
          ) : (
            <List>
              {rows.map((tx) => (
                <TransactionRow
                  key={tx.id}
                  transaction={tx}
                  onSelect={() => navigate('transactionDetail', { id: tx.id })}
                />
              ))}
            </List>
          )}
        </section>

        <div className="row">
          <Button
            variant="primary"
            disabled={saving || data.status === 'cancelledByUser'}
            onClick={() => void update({ status: 'cancelledByUser' })}
          >
            {t('recurringDetail.cta.markCancelled')}
          </Button>
          <Button variant="destructive" disabled={saving} onClick={() => void update({ userConfirmed: false })}>
            {t('recurringDetail.cta.notRecurring')}
          </Button>
        </div>
      </div>
    </Screen>
  )
}
